import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { initials } from "@/lib/format";
import { UserCog, ShieldCheck, Loader2, Plus, Ban, Trash2, RotateCcw, Pencil } from "lucide-react";

export default function Administrators() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // {mode:'promote'|'edit', admin?}
  const [form, setForm] = useState({ user_id: "", role: "manager", permissions: [], workplace_scope: [] });
  const ROLE_LABELS = { super_admin: "Super Admin", manager: "Manager", admin: "Admin", hr: "HR" };

  useEffect(() => {
    if (user && user.role !== "super_admin") { navigate("/app", { replace: true }); }
  }, [user, navigate]);

  const load = async () => {
    const [a, e, w, c] = await Promise.allSettled([
      api.get("/admins"), api.get("/employees"), api.get("/workplaces"), api.get("/permissions/catalog"),
    ]);
    if (a.status === "fulfilled") setAdmins(a.value.data);
    if (e.status === "fulfilled") setEmployees(e.value.data);
    if (w.status === "fulfilled") setWorkplaces(w.value.data);
    if (c.status === "fulfilled") setCatalog(c.value.data.permissions);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const wpName = (id) => workplaces.find((w) => w.id === id)?.name || id;
  const promotable = employees.filter((e) => e.role === "employee" && e.status === "active");

  const openPromote = () => { setForm({ user_id: "", role: "manager", permissions: [], workplace_scope: [] }); setDialog({ mode: "promote" }); };
  const openEdit = (adm) => {
    setForm({ user_id: adm.id, role: adm.role, permissions: adm.permissions || [], workplace_scope: adm.workplace_scope || [] });
    setDialog({ mode: "edit", admin: adm });
  };

  const togglePerm = (k) => setForm((f) => ({ ...f, permissions: f.permissions.includes(k) ? f.permissions.filter((p) => p !== k) : [...f.permissions, k] }));
  const toggleScope = (id) => setForm((f) => ({ ...f, workplace_scope: f.workplace_scope.includes(id) ? f.workplace_scope.filter((s) => s !== id) : [...f.workplace_scope, id] }));

  const save = async () => {
    setBusy(true);
    try {
      if (dialog.mode === "promote") {
        if (!form.user_id) { toast.error("Select an employee to promote."); setBusy(false); return; }
        const payload = { user_id: form.user_id, role: form.role, workplace_scope: form.workplace_scope };
        if (form.role === "manager") payload.permissions = form.permissions;
        await api.post("/admins", payload);
        toast.success(`${ROLE_LABELS[form.role]} added.`);
      } else {
        await api.patch(`/admins/${dialog.admin.id}`, { role: form.role, permissions: form.permissions, workplace_scope: form.workplace_scope });
        toast.success("Administrator updated.");
      }
      setDialog(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const act = async (adm, kind) => {
    try {
      if (kind === "suspend") await api.post(`/admins/${adm.id}/suspend`);
      if (kind === "activate") await api.post(`/admins/${adm.id}/activate`);
      if (kind === "remove") await api.delete(`/admins/${adm.id}`);
      toast.success("Done.");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="administrators-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-emerald-400" />
          <h1 className="font-display text-2xl font-bold">Administrators & Permissions</h1>
        </div>
        <Button onClick={openPromote} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="add-admin-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Administrator
        </Button>
      </div>
      <p className="text-sm text-muted-foreground -mt-2">Delegate access to Managers/Supervisors with specific permissions and workplace scope. Managers can never expand their own access.</p>

      <div className="grid gap-4 md:grid-cols-2">
        {admins.map((a) => {
          const isSuper = a.role === "super_admin";
          const scopeAll = !isSuper && (!a.workplace_scope || a.workplace_scope.length === 0);
          return (
            <div key={a.id} className="card-emerald p-5" data-testid="admin-card">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 font-semibold">{initials(a.name)}</div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{a.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                </div>
                <Badge className={`ml-auto ${isSuper ? "bg-emerald-500/20 text-emerald-300" : a.status === "disabled" ? "bg-red-500/20 text-red-300" : "bg-sky-500/20 text-sky-300"}`}>
                  {isSuper ? "Super Admin" : a.status === "disabled" ? "Suspended" : (ROLE_LABELS[a.role] || "Manager")}
                </Badge>
              </div>

              {isSuper ? (
                <p className="mt-4 text-sm text-emerald-200/80 flex items-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Full company access</p>
              ) : (
                <>
                  <div className="mt-4">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Permissions</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(a.permissions || []).length === 0 ? <span className="text-xs text-muted-foreground">No permissions granted</span> :
                        (a.permissions || []).map((p) => (
                          <Badge key={p} variant="outline" className="border-emerald-500/30 text-emerald-200 text-[10px]">
                            {catalog.find((c) => c.key === p)?.label || p}
                          </Badge>
                        ))}
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">Workplace scope</p>
                    <p className="text-sm">{scopeAll ? "All workplaces" : a.workplace_scope.map(wpName).join(", ")}</p>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="border-emerald-500/40" onClick={() => openEdit(a)} data-testid="edit-admin-btn"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                    {a.status !== "disabled" ? (
                      <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300" onClick={() => act(a, "suspend")} data-testid="suspend-admin-btn"><Ban className="w-3.5 h-3.5 mr-1" /> Suspend</Button>
                    ) : (
                      <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300" onClick={() => act(a, "activate")} data-testid="activate-admin-btn"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Reactivate</Button>
                    )}
                    <Button size="sm" variant="outline" className="border-red-500/40 text-red-300" onClick={() => act(a, "remove")} data-testid="remove-admin-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-[#121816] border-emerald-500/20 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "promote" ? "Add an Administrator" : `Edit ${dialog?.admin?.name}`}</DialogTitle>
            <DialogDescription>Choose a role. Managers get only the permissions you pick; Admin and HR get a sensible preset. Leave workplace scope empty for company-wide access.</DialogDescription>
          </DialogHeader>

          {dialog?.mode === "promote" && (
            <div>
              <Label>Employee</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="promote-employee-select"><SelectValue placeholder="Select an active employee" /></SelectTrigger>
                <SelectContent className="bg-[#121816] border-emerald-500/20">
                  {promotable.length === 0 ? <div className="px-3 py-2 text-sm text-muted-foreground">No promotable employees</div> :
                    promotable.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="promote-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121816] border-emerald-500/20">
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="hr">HR</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            {form.role === "admin" && <p className="text-xs text-muted-foreground mt-1.5">Admins get nearly all company permissions automatically (billing & ownership stay Super Admin only).</p>}
            {form.role === "hr" && <p className="text-xs text-muted-foreground mt-1.5">HR gets a people-focused permission set (employees, attendance, leave, payroll, reports) automatically.</p>}
          </div>

          {form.role === "manager" && (
            <div>
              <Label>Permissions</Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {catalog.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 cursor-pointer text-sm" data-testid={`perm-${c.key}`}>
                    <Checkbox checked={form.permissions.includes(c.key)} onCheckedChange={() => togglePerm(c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Workplace scope</Label>
            <p className="text-xs text-muted-foreground mb-2">Empty = all workplaces</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {workplaces.map((w) => (
                <label key={w.id} className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 cursor-pointer text-sm" data-testid={`scope-${w.id}`}>
                  <Checkbox checked={form.workplace_scope.includes(w.id)} onCheckedChange={() => toggleScope(w.id)} />
                  {w.name}
                </label>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="save-admin-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : dialog?.mode === "promote" ? "Add Administrator" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
